/**
 * The Source SSN file of an upload job: the SSN file of one Download major job of
 * the vendor its documents came from. The job form offers the candidates the
 * server lists for the chosen folder and takes the server's suggestion until the
 * operator picks; a new folder starts the choice over.
 */
import type {
  SourceSsnCandidate,
  SsnReadiness,
  SsnSourceReader,
} from "features/adminUsers/adminUsers";

/** undefined: nothing decided yet (the suggestion applies); null: no source. */
export type SourceSsnChoice = number | null | undefined;

/** The choice to show and send once the server answered for the folder. */
export function resolveSourceChoice(
  choice: SourceSsnChoice,
  candidates: SourceSsnCandidate[] | undefined,
  suggested: number | null | undefined,
): number | null {
  if (choice === null) return null;
  const ids = (candidates ?? []).map((c) => c.major_job_id);
  if (choice !== undefined && ids.includes(choice)) return choice;
  return suggested != null && ids.includes(suggested) ? suggested : null;
}

/** "ADP Download - SSN file ready", so the operator sees which file is usable. */
export function sourceCandidateLabel(candidate: SourceSsnCandidate): string {
  const file =
    candidate.ssn_file_exists === true
      ? "SSN file ready"
      : candidate.ssn_file_exists === false
        ? candidate.has_ssn_job
          ? "no SSN file yet"
          : "no SSN file yet, no SSN job"
        : "SSN file not checked yet";
  const writes = candidate.writes_into_folder
    ? ", writes into this folder"
    : "";
  return `${candidate.name} - ${file}${writes}`;
}

/** The hint under the field when nothing can be chosen. */
export function noSourceHint(sourceVendor: string | undefined): string {
  const vendor = (sourceVendor || "").trim();
  if (!vendor)
    return "The source folder names no vendor, so no SSN file can be its source.";
  return (
    `No Download major job of ${vendor} for this client - create one and run its ` +
    "SSN job; until then the job matches by names."
  );
}

/** "Paycom W2 (Paycom Upload 86A29), I-9 (Paycom Upload 86A30)". */
export function describeSourceReaders(
  readers: SsnSourceReader[] | undefined,
): string {
  return (readers ?? [])
    .map((r) =>
      r.major_job_name ? `${r.job_name} (${r.major_job_name})` : r.job_name,
    )
    .join(", ");
}

/** What the field says under itself, from what the server answered. */
export function sourceFieldHint({
  useSsnMatch,
  hasFolder,
  readiness,
}: {
  useSsnMatch: boolean;
  hasFolder: boolean;
  readiness: SsnReadiness | null;
}): string {
  if (!useSsnMatch)
    return "SSN match is off - the job matches by names and reads no SSN file.";
  if (!hasFolder) {
    return "Choose the source folder first: the Download major jobs of its vendor are listed here.";
  }
  if (!readiness) return "Checking the folder...";
  if (readiness.source_choice_error)
    return readiness.source_choice_error.message;
  if (!(readiness.source_candidates ?? []).length)
    return noSourceHint(readiness.source_vendor);
  return (
    `The SSN file of a ${readiness.source_vendor} Download major job of this client - ` +
    "the side these documents came from. Filled in when only one fits."
  );
}
